import { Link } from "react-router-dom";
import { MapPin, Phone, Clock, Star, ArrowUpRight } from "lucide-react";
import { useLiveReviews } from "../lib/useLiveReviews";

export default function LocationCard({ location, compact = false }) {
    const todayHours =
        location.hours?.[new Date().getDay() === 0 ? 6 : new Date().getDay() - 1];
    // Pull LIVE rating + count from Google Places via /api/reviews/{slug}.
    // Falls back to the static LOCATIONS value while the request is in flight.
    const { rating, count } = useLiveReviews(
        location.slug,
        location.rating,
        location.review_count
    );
    return (
        <article
            data-testid={`location-card-${location.slug}`}
            className="group relative bg-zen-bg border border-zen-ink/10 hover:border-zen-olive/60 hover:-translate-y-1 transition-all duration-500 overflow-hidden"
        >
            <div className="aspect-[16/10] overflow-hidden bg-zen-surface relative">
                <img
                    src={location.image}
                    alt={`${location.name} interior`}
                    className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-110"
                />
                <div className="absolute top-4 right-4 w-10 h-10 border border-white/40 bg-zen-ink/40 backdrop-blur-sm flex items-center justify-center text-white opacity-0 -translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
                    <ArrowUpRight size={16} />
                </div>
            </div>
            <div className="p-8 sm:p-10">
                <div className="flex items-center gap-2 mb-5">
                    <Star size={14} className="text-zen-gold" fill="#C9A567" />
                    <span
                        className="text-[11px] uppercase tracking-[0.22em] text-zen-ink-soft"
                        data-testid={`location-card-reviews-${location.slug}`}
                    >
                        {Number(rating).toFixed(1)} · {Number(count).toLocaleString()}+ reviews
                    </span>
                </div>
                <h3 className="font-serif text-2xl sm:text-3xl text-zen-ink font-light leading-tight mb-4">
                    {location.name}
                </h3>
                {!compact && (
                    <p className="text-sm text-zen-ink-soft mb-7 leading-relaxed">
                        {location.pitch}
                    </p>
                )}
                <div className="space-y-3 text-sm text-zen-ink-soft mb-8">
                    <div className="flex items-start gap-3">
                        <MapPin
                            size={14}
                            className="text-zen-olive mt-1 shrink-0"
                        />
                        <span>
                            {location.street}
                            <br />
                            {location.city}, {location.state} {location.zip}
                        </span>
                    </div>
                    <div className="flex items-center gap-3">
                        <Phone size={14} className="text-zen-olive" />
                        <a
                            href={`tel:${location.phone_tel || location.phone}`}
                            className="hover:text-zen-olive transition-colors"
                        >
                            {location.phone}
                        </a>
                    </div>
                    {todayHours && (
                        <div className="flex items-center gap-3">
                            <Clock size={14} className="text-zen-olive" />
                            <span>
                                Today · {todayHours.open} – {todayHours.close}
                            </span>
                        </div>
                    )}
                </div>
                <div className="flex flex-wrap gap-3">
                    <Link
                        to={`/locations/${location.slug}`}
                        data-testid={`location-card-details-${location.slug}`}
                        className="inline-flex items-center gap-2 bg-zen-olive hover:bg-zen-olive-dark text-zen-bg px-6 py-3 font-medium uppercase tracking-[0.22em] text-[11px] transition-colors"
                    >
                        Store details
                    </Link>
                    <Link
                        to={`/menu/${location.slug}`}
                        data-testid={`location-card-menu-${location.slug}`}
                        className="inline-flex items-center gap-2 border border-zen-ink/25 hover:border-zen-ink text-zen-ink hover:bg-zen-ink hover:text-zen-bg px-6 py-3 font-medium uppercase tracking-[0.22em] text-[11px] transition-colors"
                    >
                        Live menu →
                    </Link>
                </div>
            </div>
        </article>
    );
}
